import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';
import { FormControl, styled, Box } from '@mui/material';

import type { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import type { AttributesLevels } from './LevelOfCareSection';
import type { ReservationFormInput } from '../schema/reservationForm.schema';
import type { TripArrayFieldContext } from '../machines/createTripArrayFieldMachine';
import type { LevelOfCareContext } from '../machines/levelOfCare.machine';
import { GURNEY_BC, BARIATRIC_GURNEY_BC } from '@/data/constants';

import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';

import SelectMenu from '@/components/forms/SelectMenu';
import { GurneyForm } from './GurneyForm';
import { GurneyAvailabilityMessage } from '../messages/GurneyAvailabilityMessage';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { usePortalContext } from '@/contexts/PortalProvider';

const PREFIX = 'PassengerMobilityField';

const classes = {
  attributes: `${PREFIX}-attributes`,
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}));

export function PassengerMobilityField({
  index,
  availableAttributes,
}: {
  index: number;
  availableAttributes: AttributesLevels;
}) {
  const t = useCommonTranslation();
  const { control, setValue, getValues, trigger } =
    useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const { isMemberPortal } = usePortalContext();
  const actor = fieldRefs.passengerMobility;
  const levelOfCareActor = fieldRefs.levelOfCare;
  const levelOfCareSnapshot = useFieldActorSnapshot(levelOfCareActor);
  const levelOfCareCtx = levelOfCareSnapshot.context as LevelOfCareContext;
  const isNonAmbulatory = levelOfCareCtx.isNonAmbulatoryByIndex[index] ?? false;
  const isGurneySelected = levelOfCareCtx.isGurneySelected[index] ?? false;
  const reverseMap = levelOfCareCtx.reverseTransportationAttributeMap;

  const isGurneyUuid = (uuid: string): boolean => {
    if (!uuid || !reverseMap) return false;
    return (
      uuid === reverseMap[GURNEY_BC] || uuid === reverseMap[BARIATRIC_GURNEY_BC]
    );
  };

  const emptyAttribute: TransportationAttribute = useMemo(
    () => ({
      transportationAttributeCategoryBC: '',
      name: t('reservation.noAttribute'),
      description: '',
      isPassengerSelectable: true,
      isDefault: false,
    }),
    [t],
  );

  const watched =
    useWatch({
      control,
      name: `trips.${index}.passengerMobility`,
    }) ?? '';

  useEffect(() => {
    const ctx = actor.getSnapshot().context as TripArrayFieldContext;
    if (ctx.values[index] !== watched) {
      actor.send({ type: 'CHANGE', index, value: watched });
    }
  }, [actor, index, watched]);

  const resetDescendantAttributeLevels = (parentLevel: 1 | 2 | 3 | 4 | 5) => {
    for (let level = parentLevel + 1; level <= 6; level++) {
      setValue(
        `trips.${index}.AttributeLevel${level}` as FieldPath<ReservationFormInput>,
        '',
        { shouldDirty: true, shouldValidate: true },
      );
    }
  };

  const level2Attributes = useMemo(() => {
    const lvl2Attr = availableAttributes.level2 || [];
    if (!isMemberPortal) return lvl2Attr;

    return lvl2Attr?.filter(
      (attr) => !isGurneyUuid(attr.transportationAttributeUUID as string),
    );
  }, [isMemberPortal, availableAttributes.level2]);

  return (
    <Controller
      name={`trips.${index}.passengerMobility`}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth required error={Boolean(fieldState.error)}>
          <StyledBox className={classes.container} component="fieldset">
            <legend>{t('reservation.passengerMobility')}</legend>
            {availableAttributes.level1 && (
              <SelectMenu
                label={t('reservation.selectAttribute')}
                name={`trips.${index}.AttributeLevel1`}
                values={availableAttributes.level1}
                valueAttr="transportationAttributeUUID"
                labelAttr="name"
                required={true}
                control={control}
                rules={{ required: true }}
                onFocus={() =>
                  levelOfCareActor.send({ type: 'FOCUS_MOBILITY', index })
                }
                onBlur={() => {
                  levelOfCareActor.send({ type: 'BLUR_MOBILITY', index });
                  field.onBlur();
                }}
                onChange={(evt) => {
                  const v = String(evt.target.value);
                  resetDescendantAttributeLevels(1);
                  actor.send({ type: 'CHANGE', index, value: v });
                  field.onChange(v);
                  // Re-validate this leg so other required fields (e.g.
                  // reasonForTrip) surface their resolver errors after the
                  // mobility/assistance dropdowns — typically the last fields
                  // the user touches — are changed. Scoped to this leg so it
                  // doesn't surface errors on other trip legs.
                  trigger(`trips.${index}`);
                }}
              />
            )}
            {availableAttributes.level2 && (
              <>
                <SelectMenu
                  label={t('reservation.selectAttribute')}
                  name={`trips.${index}.AttributeLevel2`}
                  values={[emptyAttribute, ...level2Attributes]}
                  valueAttr="transportationAttributeUUID"
                  labelAttr="name"
                  required={isNonAmbulatory}
                  control={control}
                  rules={isNonAmbulatory ? { required: true } : undefined}
                  onChange={(evt) => {
                    const v = String(evt.target.value);
                    const currentL2 = String(
                      getValues(`trips.${index}.AttributeLevel2`) ?? '',
                    );
                    if (isGurneyUuid(currentL2) && isGurneyUuid(v)) {
                      setValue(`trips.${index}.AttributeLevel3`, '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setValue(`trips.${index}.AttributeLevel6`, '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    } else {
                      resetDescendantAttributeLevels(2);
                    }
                    actor.send({ type: 'CHANGE', index, value: v });
                    field.onChange(v);
                    trigger(`trips.${index}`);
                  }}
                />
                {isGurneySelected && (
                  <GurneyAvailabilityMessage index={index} />
                )}
              </>
            )}
            {availableAttributes.level3 && !isGurneySelected && (
              <SelectMenu
                label={t('reservation.selectAttribute')}
                name={`trips.${index}.AttributeLevel3`}
                values={[emptyAttribute, ...availableAttributes.level3]}
                valueAttr="transportationAttributeUUID"
                labelAttr="name"
                required={false}
                control={control}
                onChange={(evt) => {
                  const v = String(evt.target.value);
                  resetDescendantAttributeLevels(3);
                  actor.send({ type: 'CHANGE', index, value: v });
                  field.onChange(v);
                  trigger(`trips.${index}`);
                }}
              />
            )}
            {availableAttributes.level4 && !isGurneySelected && (
              <SelectMenu
                label={t('reservation.selectAttribute')}
                name={`trips.${index}.AttributeLevel4`}
                values={[emptyAttribute, ...availableAttributes.level4]}
                valueAttr="transportationAttributeUUID"
                labelAttr="name"
                required={false}
                control={control}
                onChange={(evt) => {
                  const v = String(evt.target.value);
                  resetDescendantAttributeLevels(4);
                  actor.send({ type: 'CHANGE', index, value: v });
                  field.onChange(v);
                  trigger(`trips.${index}`);
                }}
              />
            )}
            {availableAttributes.level5 && !isGurneySelected && (
              <SelectMenu
                label={t('reservation.selectAttribute')}
                name={`trips.${index}.AttributeLevel5`}
                values={[emptyAttribute, ...availableAttributes.level5]}
                valueAttr="transportationAttributeUUID"
                labelAttr="name"
                required={false}
                control={control}
                onChange={(evt) => {
                  const v = String(evt.target.value);
                  resetDescendantAttributeLevels(5);
                  actor.send({ type: 'CHANGE', index, value: v });
                  field.onChange(v);
                  trigger(`trips.${index}`);
                }}
              />
            )}
            {availableAttributes.level6 && (
              <SelectMenu
                label={t('reservation.selectAttribute')}
                name={`trips.${index}.AttributeLevel6`}
                values={[emptyAttribute, ...availableAttributes.level6]}
                valueAttr="transportationAttributeUUID"
                labelAttr="name"
                required={false}
                control={control}
                onChange={(evt) => {
                  const v = String(evt.target.value);
                  actor.send({ type: 'CHANGE', index, value: v });
                  field.onChange(v);
                  trigger(`trips.${index}`);
                }}
              />
            )}
            {isGurneySelected && <GurneyForm index={index} />}
          </StyledBox>
        </FormControl>
      )}
    />
  );
}
